// src/components/resumo/PageLimitModal.tsx

import React, { useEffect, useRef } from 'react';
import { LockKeyhole, ExternalLink, X } from 'lucide-react';
import './PageLimitModal.css';

interface PageLimitModalProps {
    isOpen: boolean;
    visiblePagesCount: number;
    darkMode?: boolean;
    onClose: () => void;
    // Overrides opcionais — usados pela prévia pública (ApostilaPreviewPage,
    // via ResumoReadPage), onde "adquira o plano completo" / "/marketplace"
    // não fazem sentido pra um visitante anônimo sem conta ainda.
    title?: string;
    description?: React.ReactNode;
    ctaLabel?: string;
    onCtaClick?: () => void;
}

const PageLimitModal: React.FC<PageLimitModalProps> = ({
    isOpen,
    visiblePagesCount,
    darkMode = false,
    onClose,
    title,
    description,
    ctaLabel,
    onCtaClick,
}) => {
    const closeBtnRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => closeBtnRef.current?.focus(), 50);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className={`plm-overlay ${darkMode ? 'dark' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="plm-title"
            aria-describedby="plm-desc"
            onClick={onClose}
        >
            <div
                className="plm-card"
                onClick={e => e.stopPropagation()}
            >
                {/* Botão fechar */}
                <button
                    ref={closeBtnRef}
                    className="plm-close-btn"
                    onClick={onClose}
                    aria-label="Fechar modal"
                >
                    <X size={18} strokeWidth={2} />
                </button>

                {/* Ícone */}
                <div className="plm-icon-wrap">
                    <LockKeyhole size={34} strokeWidth={1.6} />
                </div>

                {/* Título */}
                <h2 id="plm-title" className="plm-title">
                    {title || 'Amostra encerrada'}
                </h2>

                {/* Descrição */}
                <p id="plm-desc" className="plm-desc">
                    {description || (
                        <>
                            Você visualizou as <strong>{visiblePagesCount}</strong> páginas disponíveis
                            na amostra gratuita. Para acessar toda a apostila, adquira o plano completo.
                        </>
                    )}
                </p>

                {/* Ação */}
                <button
                    className="plm-btn-primary"
                    onClick={onCtaClick || (() => window.open('/marketplace', '_blank', 'noopener,noreferrer'))}
                >
                    <ExternalLink size={15} strokeWidth={2} />
                    {ctaLabel || 'Ver planos'}
                </button>
            </div>
        </div>
    );
};

export default PageLimitModal;